/**
 * TimezoneChangeDrawer
 * 
 * A bottom drawer that appears when the app detects the user's device timezone
 * differs from their stored notification timezone. This typically happens when
 * a user travels to a different timezone.
 * 
 * Shows a friendly travel-themed prompt asking if they want to update their
 * notification schedule to match the new timezone.
 */

import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import type { TimezoneChange } from '@/lib/hooks/use-timezone-detection'

interface TimezoneChangeDrawerProps {
  change: TimezoneChange | null
  isUpdating: boolean
  onApprove: () => void
  onDismiss: () => void
  onNeverShowAgain: () => void
}

export function TimezoneChangeDrawer({
  change,
  isUpdating,
  onApprove,
  onDismiss,
  onNeverShowAgain,
}: TimezoneChangeDrawerProps) {
  const navigate = useNavigate()
  if (!change) return null

  return (
    <Drawer open={!!change} onOpenChange={(open) => { if (!open) onDismiss() }}>
      <DrawerContent>
        <div className="mx-auto w-full max-w-md px-4 pb-2">
          <DrawerHeader className="text-center pb-2">
            {/* Travel illustration */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="flex justify-center mb-2"
            >
              <div className="relative">
                <FluentEmoji emoji="✈️" size={48} />
                <motion.div
                  className="absolute -bottom-1 -right-2"
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <FluentEmoji emoji="🌍" size={24} />
                </motion.div>
              </div>
            </motion.div>

            <DrawerTitle className="text-lg font-bold text-slate-900 dark:text-white">
              New timezone detected
            </DrawerTitle>
            <DrawerDescription className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Looks like you might be traveling! Update your notification schedule?
            </DrawerDescription>
          </DrawerHeader>

          {/* Timezone comparison card */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 overflow-hidden">
            {/* From timezone */}
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Now</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                  {change.storedTimezoneLabel}
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  {change.storedOffset}
                </p>
              </div>
            </div>

            {/* Arrow divider */}
            <div className="relative px-4">
              <div className="border-t border-dashed border-slate-200 dark:border-slate-700" />
              <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-50 dark:bg-slate-900/50 px-2">
                <motion.div
                  animate={{ y: [0, 3, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-emerald-500">
                    <path d="M8 2v12M8 14l4-4M8 14L4 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </motion.div>
              </div>
            </div>

            {/* To timezone */}
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">New</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300 truncate">
                  {change.deviceTimezoneLabel}
                </p>
                <p className="text-xs text-emerald-500 dark:text-emerald-500">
                  {change.deviceOffset}
                </p>
              </div>
            </div>
          </div>

          {/* Explanation */}
          <p className="text-xs text-center text-slate-400 dark:text-slate-500 mt-3 px-2">
            This ensures your reminders arrive at the right local time.
          </p>

          {/* Actions */}
          <DrawerFooter className="flex flex-col gap-2 pt-3 pb-4">
            {/* Primary: Update now */}
            <Button
              onClick={onApprove}
              disabled={isUpdating}
              className="w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
            >
              {isUpdating ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                />
              ) : (
                <>Update to {change.deviceTimezoneLabel}</>
              )}
            </Button>
            
            {/* Secondary: Go to settings to change manually */}
            <Button
              variant="outline"
              onClick={() => {
                onDismiss()
                navigate('/settings?section=notifications')
              }}
              disabled={isUpdating}
              className="w-full h-10 rounded-xl text-sm"
            >
              Change it in Settings
            </Button>

            {/* Tertiary row: dismiss + never show again */}
            <div className="flex items-center justify-center gap-3 pt-1">
              <button
                onClick={onDismiss}
                disabled={isUpdating}
                className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                Not now
              </button>
              <span className="text-slate-200 dark:text-slate-700">|</span>
              <button
                onClick={onNeverShowAgain}
                disabled={isUpdating}
                className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                Don&apos;t show this again
              </button>
            </div>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
